import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { workspaces } from '../store/seed';
import type { Channel } from '../types';

const PLATFORM_MAX_POSTS_PER_DAY = 3;
const AGENCY_DEFAULT_POSTS_PER_DAY = 1;
const SUGGESTED_OVERRIDE_POSTS_PER_DAY = 2;

export default function PlatformSlotSettings({
  workspaceId,
  onWorkspaceChange,
}: {
  workspaceId: string;
  onWorkspaceChange: (id: string) => void;
}) {
  const { translate } = useApp();
  const { notify } = useUi();
  const { commands } = useDemo();
  const { workspace, accounts, dailyLimitOverrides } = useWorkspaceData(workspaceId);
  const [isEditingOverride, setIsEditingOverride] = useState(false);
  const [channel, setChannel] = useState<Channel>(workspace.channels[0]);
  const [value, setValue] = useState(SUGGESTED_OVERRIDE_POSTS_PER_DAY);
  const perDay = (postsPerDay: number) => translate('{n} per day', { n: postsPerDay });
  const isConnected = (workspaceChannel: Channel) =>
    accounts.some(
      (account) =>
        account.network === workspaceChannel &&
        !/personal/i.test(account.type) &&
        account.health === 'Connected'
    );
  function saveOverride() {
    commands.setSlotOverride(
      workspace.id,
      channel,
      value === AGENCY_DEFAULT_POSTS_PER_DAY ? null : value
    );
    setIsEditingOverride(false);
    notify(
      translate('{ws}: {channel} is now {value}. The client sees it in Plan allowance.', {
        ws: workspace.name,
        channel,
        value: perDay(value),
      })
    );
  }
  function removeOverride(workspaceChannel: Channel) {
    commands.setSlotOverride(workspace.id, workspaceChannel, null);
    notify(translate('{channel} is back to the agency default.', { channel: workspaceChannel }));
  }
  function startOverride(workspaceChannel: Channel) {
    setChannel(workspaceChannel);
    setValue(SUGGESTED_OVERRIDE_POSTS_PER_DAY);
    setIsEditingOverride(true);
  }
  const postsPerDayOptions = Array.from(
    { length: PLATFORM_MAX_POSTS_PER_DAY + 1 },
    (_, postsPerDay) => postsPerDay
  );
  return (
    <div className="panel" id="platform-limits">
      <div className="panel-header">
        <h2>{translate('Daily publishing limits')}</h2>
        <select
          className="select-field"
          aria-label={translate('Workspace')}
          value={workspaceId}
          onChange={(event) => {
            onWorkspaceChange(event.target.value);
            setIsEditingOverride(false);
          }}
        >
          {workspaces.map((workspaceOption) => (
            <option key={workspaceOption.id} value={workspaceOption.id}>
              {workspaceOption.name}
            </option>
          ))}
        </select>
      </div>
      <p className="muted small-print">
        {translate(
          'Effective value = the workspace override if one is set, otherwise the agency default, never more than the platform maximum. It is what {ws} sees in its calendar and Plan allowance.',
          { ws: workspace.name }
        )}
      </p>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{translate('Channel')}</th>
              <th>{translate('Platform maximum')}</th>
              <th>{translate('Agency default (Hakusana)')}</th>
              <th>{translate('Workspace override')}</th>
              <th>{translate('Effective value')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {workspace.channels.map((workspaceChannel) => {
              const override = dailyLimitOverrides[workspaceChannel];
              const hasOverride = override !== undefined;
              return (
                <tr key={workspaceChannel}>
                  <td>
                    <strong>{workspaceChannel}</strong>
                    {!isConnected(workspaceChannel) && (
                      <div className="field-warn">
                        {translate('Not connected: nothing can be published until it is')}
                      </div>
                    )}
                  </td>
                  <td>{perDay(PLATFORM_MAX_POSTS_PER_DAY)}</td>
                  <td>{perDay(AGENCY_DEFAULT_POSTS_PER_DAY)}</td>
                  <td>{hasOverride ? perDay(override) : translate('None')}</td>
                  <td>
                    <strong>{perDay(override ?? AGENCY_DEFAULT_POSTS_PER_DAY)}</strong>
                    {hasOverride && (
                      <div className="muted small-print">{translate('from the override')}</div>
                    )}
                  </td>
                  <td>
                    {hasOverride ? (
                      <button
                        className="button small ghost"
                        onClick={() => removeOverride(workspaceChannel)}
                      >
                        {translate('Remove override')}
                      </button>
                    ) : (
                      <button
                        className="button small"
                        onClick={() => startOverride(workspaceChannel)}
                      >
                        {translate('Override')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isEditingOverride && (
        <div className="panel inset override-editor">
          <h3>{translate('Override the limit for {ws}', { ws: workspace.name })}</h3>
          <div className="business-grid">
            <label className="wizard-field">
              {translate('Channel')}
              <select
                className="select-field"
                value={channel}
                onChange={(event) => setChannel(event.target.value as Channel)}
              >
                {workspace.channels.map((workspaceChannel) => (
                  <option key={workspaceChannel}>{workspaceChannel}</option>
                ))}
              </select>
            </label>
            <label className="wizard-field">
              {translate('Posts per day')}
              <select
                className="select-field"
                value={value}
                onChange={(event) => setValue(Number(event.target.value))}
              >
                {postsPerDayOptions.map((postsPerDay) => (
                  <option key={postsPerDay} value={postsPerDay}>
                    {postsPerDay === 0 ? translate('0 (channel paused)') : perDay(postsPerDay)}
                  </option>
                ))}
              </select>
              <span className="muted small-print">
                {translate('Up to the platform maximum of {n}.', { n: PLATFORM_MAX_POSTS_PER_DAY })}
              </span>
            </label>
          </div>
          <p className="small-print">
            {translate(
              'Result: {channel} goes from {from} to {to} for {ws}. Changes apply to new bookings; scheduled posts are kept.',
              {
                channel,
                from: perDay(dailyLimitOverrides[channel] ?? AGENCY_DEFAULT_POSTS_PER_DAY),
                to: perDay(value),
                ws: workspace.name,
              }
            )}
          </p>
          <p className="muted small-print">
            {translate(
              'Prototype note: the new limit shows in the workspace’s Plan allowance and daily allowance. The calendar’s booking rules stay one post per channel per day until the backend applies limits.'
            )}
          </p>
          <div className="button-row">
            <button className="button primary" onClick={saveOverride}>
              <SafeIcon name="FiCheck" />
              {translate('Save override')}
            </button>
            <button className="button" onClick={() => setIsEditingOverride(false)}>
              {translate('Cancel')}
            </button>
          </div>
        </div>
      )}
      {!isEditingOverride && (
        <p className="muted small-print" style={{ marginTop: 12 }}>
          {translate(
            'Unused days never carry over. Changes would apply to new bookings; scheduled posts are kept.'
          )}
        </p>
      )}
    </div>
  );
}
